import React, { useState, useEffect } from 'react';
import { Mail } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNotificationSound } from '../../hooks/useNotificationSound.js';
import { useAuth } from '@/components/Auth/AuthContext';
import { getAgeBadgeStyle } from '@/utils/ageBadge';
import { getCompanyAiFocusDomId, matchesCompanyAiFocusRecord, readCompanyAiFocusTarget, readCompanyAiFollowUpSeed, readCompanyAiPeriodFilter } from '@/utils/companyAiNavigation';
import { confirmDeletionByTyping } from '@/utils/deleteConfirmation';
import { isAcceptedQuotationLike } from '@/utils/quotationAcceptance';
import { getQuotationDesignFeeTotal, getQuotationStructuralFeePerUnit, getQuotationStructuralFeeTotal } from '@/utils/quotationFees';
import { hasDateRange, normalizeDateOnlyInput } from '@/utils/listDateFilters';
import { LIST_SEARCH_DEBOUNCE_MS } from '@/constants/search';
import PageHeader from '@/components/Layout/PageHeader';
import { useIsNarrowScreen } from '@/hooks/useMediaQuery';
import { getPrimaryRoleLabel } from '@/utils/swaRole';
import { replaceInternalOwnerEmails } from '@/utils/internalOwnerLabel';
import { formatSuburbLabel } from '@/utils/suburbText';
import { Button, Card, DataRow, EmptyState, Input, PageSection, Select, StatusBadge } from '@/ui/components';
import ListPaginationControls from '@/components/List/ListPaginationControls';
import EmailSendModal, { type EmailModalTarget } from '@/components/Email/EmailSendModal';
import '@/pages/dataGridFocus.css';

type QuotationSortBy = 'quotationNumber' | 'createdDate' | 'structuralFee' | 'civilFee' | 'status';
type SortDirection = 'asc' | 'desc';
type BlockedQuotationFolder = {
  quotationNumber: string;
  folderPath?: string | null;
  webUrl?: string | null;
};
const LIST_PAGE_SIZE = 20;
const LIST_PAGE_SIZE_OPTIONS = [20, 50, 100];
const DELETE_CONCURRENCY = 3;
const WILSON_EMAIL = 'wd@linfinityeng.com';
const YUAN_EMAIL = 'yl@linfinityeng.com';

function getDefaultSortDirection(sortBy: QuotationSortBy): SortDirection {
  return 'desc';
}

function normalizeListQueryInput(value: unknown) {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeQuotationIdentityValues(values: unknown[]) {
  return values
    .map((value) => String(value ?? '').trim().toLowerCase())
    .filter(Boolean);
}

function quotationRowIdentityValues(quotation: any) {
  return normalizeQuotationIdentityValues([
    quotation?.id,
    quotation?.quotationId,
    quotation?.QuotationId,
  ]);
}

function quotationNumberIdentityValues(quotation: any) {
  return normalizeQuotationIdentityValues([
    quotation?.quotationNumber,
    quotation?.QuotationNumber,
    quotation?.QuotationNo,
  ]);
}

export function findReconciledAcceptedQuotation(target: any, refreshedQuotations: any[]) {
  const targetRowIdentities = new Set(quotationRowIdentityValues(target));
  const targetNumberIdentities = new Set(quotationNumberIdentityValues(target));
  if (targetRowIdentities.size === 0 && targetNumberIdentities.size === 0) return null;

  return refreshedQuotations.find((quotation) => {
    if (!isAcceptedQuotationLike(quotation)) return false;

    const rowMatches = quotationRowIdentityValues(quotation)
      .some((identity) => targetRowIdentities.has(identity));
    if (targetRowIdentities.size > 0) return rowMatches;

    const numberMatches = quotationNumberIdentityValues(quotation)
      .some((identity) => targetNumberIdentities.has(identity));

    return numberMatches;
  }) ?? null;
}

const QuotationList: React.FC = () => {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [actionBlockedFolders, setActionBlockedFolders] = useState<BlockedQuotationFolder[]>([]);
  const [actionBusy, setActionBusy] = useState<null | 'folder' | 'delete' | 'accept'>(null);
  const [acceptingQuotationId, setAcceptingQuotationId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState<QuotationSortBy>('createdDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>(getDefaultSortDirection('createdDate'));
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'pending' | 'approved' | 'rejected'>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(LIST_PAGE_SIZE);
  const [pagination, setPagination] = useState({ page: 1, pageSize: LIST_PAGE_SIZE, totalRecords: 0, totalPages: 1 });
  const [selectMode, setSelectMode] = useState(false);
  const [selectedQuotations, setSelectedQuotations] = useState<Set<string>>(new Set());
  const [emailTarget, setEmailTarget] = useState<EmailModalTarget | null>(null);
  const { playSuccess, playError } = useNotificationSound();
  const { login, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const roleLabel = getPrimaryRoleLabel(user?.userRoles, user?.userDetails);
  const isNarrowScreen = useIsNarrowScreen(640);
  const currentUserEmail = String(user?.userDetails || '').trim().toLowerCase();
  const aiFocus = readCompanyAiFocusTarget(location.state, 'quotations');
  const aiPeriodFilter = readCompanyAiPeriodFilter(location.state, 'quotations');
  const aiFollowUp = readCompanyAiFollowUpSeed(location.state);
  const hasActiveDateRange = hasDateRange(dateFrom, dateTo);
  const activeDateBasis = aiPeriodFilter?.dateBasis || 'issue date';
  const isUsingAiPeriodRange = Boolean(
    aiPeriodFilter
    && normalizeDateOnlyInput(aiPeriodFilter.startDate) === dateFrom
    && normalizeDateOnlyInput(aiPeriodFilter.endDate) === dateTo
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadQuotations(searchQuery.trim());
    }, searchQuery.trim() ? LIST_SEARCH_DEBOUNCE_MS : 0);

    return () => window.clearTimeout(timer);
  }, [searchQuery, dateFrom, dateTo, aiPeriodFilter?.startDate, aiPeriodFilter?.endDate, aiPeriodFilter?.dateBasis, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, dateFrom, dateTo, activeDateBasis, statusFilter]);

  useEffect(() => {
    if (!aiFocus && !aiPeriodFilter) {
      return;
    }

    setStatusFilter('all');
    if (aiPeriodFilter) {
      setSearchQuery('');
      setDateFrom(normalizeDateOnlyInput(aiPeriodFilter.startDate));
      setDateTo(normalizeDateOnlyInput(aiPeriodFilter.endDate));
      return;
    }

    setSearchQuery((current) => current.trim() || aiFocus.searchText);
  }, [aiFocus?.recordId, aiFocus?.searchText, aiPeriodFilter?.startDate, aiPeriodFilter?.endDate, aiPeriodFilter?.dateBasis]);

  const loadQuotations = async (
    rawQuery: unknown = searchQuery.trim(),
    options: { silent?: boolean; forceRefresh?: boolean } = {}
  ) => {
    const query = normalizeListQueryInput(rawQuery);
    const silent = Boolean(options.silent);
    const forceRefresh = Boolean(options.forceRefresh);
    if (!silent) {
      setLoading(true);
      setError(null);
    }

    try {
      console.log('=== STARTING QUOTATION LOAD ===');
      console.log('User authenticated:', !!user);

      // Dynamic import to ensure AuthContext is initialized first
      const { azureSqlService } = await import('@/services/azureSqlService.js');

      // Get quotations from Azure SQL Database
      const quotationResult = await azureSqlService.getQuotations({
        page,
        pageSize,
        query: {
          ...(query ? { q: query } : {}),
          ...(dateFrom ? { dateFrom } : {}),
          ...(dateTo ? { dateTo } : {}),
          ...(hasActiveDateRange ? { dateBasis: activeDateBasis } : {}),
        },
        useCache: !forceRefresh && !query && !hasActiveDateRange && page === 1,
        returnEnvelope: true,
      });
      const quotationData = quotationResult?.data || [];

      const normalizePostCode = (value: any) => {
        if (value === null || value === undefined) return '';
        // SQL sometimes returns postcodes like "3815.0" - normalize for display/search.
        if (typeof value === 'number') return String(Math.trunc(value));
        const str = String(value).trim();
        const n = Number(str);
        if (!Number.isNaN(n) && Number.isFinite(n)) return String(Math.trunc(n));
        return str;
      };

      const buildProjectLocation = (q: any) => {
        const address = q.Address || q.ProjectLocation || '';
        const suburb = formatSuburbLabel(q.Suburb || '');
        const postCode = normalizePostCode(q.PostCode);
        return [address, suburb, postCode].filter(Boolean).join(', ');
      };

      const normalizeStatus = (q: any) => {
        if (isAcceptedQuotationLike(q)) return { status: 'Approved', statusCode: '3' };
        const raw = (q.Status || q.Acceptance || '').toString().trim().toLowerCase();
        if (raw === 'accepted' || raw === 'approved') return { status: 'Approved', statusCode: '3' };
        if (raw === 'rejected') return { status: 'Rejected', statusCode: '4' };
        if (raw === 'pending' || raw === 'sent') return { status: 'Pending', statusCode: '2' };
        if (raw === 'draft') return { status: 'Draft', statusCode: '1' };
        return { status: q.Status || q.Acceptance || 'Pending', statusCode: q.StatusCode || '2' };
      };

      // Map API response to component format (support both old/new API shapes)
      const mappedQuotations = quotationData.map((q: any) => ({
        id: (q.QuotationId ?? q.quotationId ?? q.id ?? q.QuotationNumber ?? q.quotationNumber)?.toString(),
        quotationId: q.QuotationId ?? q.quotationId ?? q.id ?? null,
        quotationNumber: q.QuotationNumber || q.QuotationNo || 'N/A',
        clientName: replaceInternalOwnerEmails(q.ClientName || q.Client || 'Unknown'),
        companyName: replaceInternalOwnerEmails(q.CompanyName || q.ClientCompany || ''),
        address: replaceInternalOwnerEmails(q.Address || ''),
        suburb: replaceInternalOwnerEmails(formatSuburbLabel(q.Suburb || '')),
        postCode: normalizePostCode(q.PostCode),
        projectLocation: replaceInternalOwnerEmails(buildProjectLocation(q) || q.ProjectLocation || 'N/A'),
        structuralFee: getQuotationStructuralFeeTotal(q),
        structuralFeeTotal: getQuotationStructuralFeeTotal(q),
        structuralFeePerUnit: getQuotationStructuralFeePerUnit(q),
        civilFee: Number(q.CivilFee || 0) || 0,
        quotationAmount: getQuotationDesignFeeTotal(q),
        createdDate: q.CreatedDate || q.Created || q.Date,
        issueDate: q.IssueDate || q.Date || q.CreatedDate,
        acceptance: isAcceptedQuotationLike(q) ? 'Accepted' : (q.Acceptance || q.Status || 'Pending'),
        noUnits: q.NoUnits ?? null,
        council: replaceInternalOwnerEmails(q.Council ?? null),
        projectScope: replaceInternalOwnerEmails(q.ProjectScope ?? null),
        projectType: replaceInternalOwnerEmails(q.ProjectType ?? null),
        broughtBy: replaceInternalOwnerEmails(q.BroughtBy ?? null),
        certifiedBy: replaceInternalOwnerEmails(q.CertifiedBy ?? null),
        ...normalizeStatus(q),
        serviceDescription: replaceInternalOwnerEmails(q.ProjectScope || q.ProjectType || q.ServiceDescription || q.Council || q.Address || 'Engineering Services')
      }));

      setQuotations(mappedQuotations);
      setPagination({
        page: Number(quotationResult?.pagination?.page || page),
        pageSize: Number(quotationResult?.pagination?.pageSize || pageSize),
        totalRecords: Number(quotationResult?.pagination?.totalRecords || mappedQuotations.length || 0),
        totalPages: Math.max(Number(quotationResult?.pagination?.totalPages || 1), 1),
      });
      if (!silent) {
        playSuccess();
      }
      console.log('✅ Quotations loaded successfully from Azure SQL:', mappedQuotations.length);
      return mappedQuotations;
    } catch (err) {
      console.error('❌ Error loading quotations:', err);
      console.error('Error type:', typeof err);
      console.error('Error stack:', err?.stack);

      // Extract detailed error information
      let errorMessage = 'Unknown error';
      let errorDetails = null;

      if (err instanceof Error) {
        errorMessage = err.message;
        // Check if error has additional details from API response
        if ((err as any).details) {
          errorDetails = (err as any).details;
          console.error('Error details from API:', errorDetails);

          // Use server message if available
          if (errorDetails.message) {
            errorMessage = errorDetails.message;
          }
        }
      } else {
        errorMessage = String(err);
      }

      if (!silent) {
        setError(errorMessage);
        playError();
      }
      return null;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const formatMoney = (amount?: number) => {
    const value = typeof amount === 'number' && Number.isFinite(amount) ? amount : 0;
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      maximumFractionDigits: 0,
    }).format(value);
  };

  const daysSince = (dateString?: string) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return null;
    const ms = Date.now() - date.getTime();
    return ms >= 0 ? Math.floor(ms / (1000 * 60 * 60 * 24)) : 0;
  };

  // Filter quotations based on search query
  const filteredQuotations = quotations
    .filter((quotation: any) => {
      if (statusFilter === 'all') return true;
      const code = String(quotation.statusCode || '').toLowerCase();
      if (statusFilter === 'draft') return code === '1';
      if (statusFilter === 'pending') return code === '2';
      if (statusFilter === 'approved') return code === '3';
      if (statusFilter === 'rejected') return code === '4';
      return true;
    })
    .filter((quotation: any) => {
      return true;
    });

  const sortedQuotations = [...filteredQuotations].sort((a: any, b: any) => {
    const compareText = (left: string, right: string) => (
      left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' })
    );
    const compareNumber = (left: number, right: number) => left - right;
    const compareDate = (left?: string, right?: string) => {
      const leftTime = left ? new Date(left).getTime() : 0;
      const rightTime = right ? new Date(right).getTime() : 0;
      const safeLeft = Number.isFinite(leftTime) ? leftTime : 0;
      const safeRight = Number.isFinite(rightTime) ? rightTime : 0;
      return safeLeft - safeRight;
    };

    let comparison = 0;
    switch (sortBy) {
      case 'quotationNumber':
        comparison = compareText(String(a.quotationNumber || ''), String(b.quotationNumber || ''));
        break;
      case 'createdDate':
        comparison = compareDate(a.createdDate, b.createdDate);
        break;
      case 'structuralFee':
        comparison = compareNumber(Number(a.structuralFee || 0), Number(b.structuralFee || 0));
        break;
      case 'civilFee':
        comparison = compareNumber(Number(a.civilFee || 0), Number(b.civilFee || 0));
        break;
      case 'status':
        comparison = compareText(String(a.acceptance || a.status || ''), String(b.acceptance || b.status || ''));
        break;
      default:
        comparison = 0;
    }

    return sortDirection === 'desc' ? comparison * -1 : comparison;
  });

  const handleSortChange = (value: string) => {
    const nextSortBy = value as QuotationSortBy;
    setSortBy(nextSortBy);
    setSortDirection(getDefaultSortDirection(nextSortBy));
  };

  const toggleSortDirection = () => {
    setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
  };

  const visibleQuotations = sortedQuotations;

  useEffect(() => {
    if (!aiFocus || loading) {
      return;
    }

    const focusId = getCompanyAiFocusDomId('quotations', aiFocus.recordId);
    if (!focusId || !visibleQuotations.some((quotation: any) => matchesCompanyAiFocusRecord(aiFocus, quotation.quotationId ?? quotation.id))) {
      return;
    }

    const timer = window.setTimeout(() => {
      const element = document.getElementById(focusId);
      if (!element) {
        return;
      }

      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (typeof (element as HTMLElement).focus === 'function') {
        (element as HTMLElement).focus({ preventScroll: true });
      }
    }, 120);

    return () => window.clearTimeout(timer);
  }, [aiFocus, loading, visibleQuotations]);
  const selectedQuotationRecords = sortedQuotations.filter((quotation: any) => selectedQuotations.has(String(quotation.id)));

  const isAcceptedQuotation = (quotation: any) => {
    return isAcceptedQuotationLike(quotation);
  };

  const isPendingQuotation = (quotation: any) => {
    const normalized = String(quotation.statusCode || quotation.acceptance || quotation.status || '').trim().toLowerCase();
    return normalized === '2' || normalized === 'pending' || normalized === 'sent';
  };

  const toggleSelectMode = () => {
    setSelectMode((v) => {
      const next = !v;
      if (!next) {
        setSelectedQuotations(new Set());
        setActionError(null);
        setActionNotice(null);
      }
      return next;
    });
  };

  const toggleSelectAll = (checked: boolean) => {
    if (!checked) {
      setSelectedQuotations(new Set());
      return;
    }
    const all = new Set<string>();
    visibleQuotations.forEach((q: any) => all.add(String(q.id)));
    setSelectedQuotations(all);
  };

  const toggleSelection = (quotationId: string) => {
    const newSelection = new Set(selectedQuotations);
    if (newSelection.has(quotationId)) {
      newSelection.delete(quotationId);
    } else {
      newSelection.add(quotationId);
    }
    setSelectedQuotations(newSelection);
  };

  const handleCreateFolders = async (targets: any[]) => {
    if (!targets.length || actionBusy) return;

    setActionBusy('folder');
    setActionError(null);
    setActionNotice(null);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const failures: string[] = [];
      let successCount = 0;

      for (const quotation of targets) {
        try {
          await azureSqlService.createWorkflowQuotationFolder({
            quotationId: quotation.quotationId ?? quotation.id,
            quotationNumber: quotation.quotationNumber,
          });
          successCount += 1;
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          failures.push(`${quotation.quotationNumber}: ${message}`);
        }
      }

      if (successCount > 0) {
        playSuccess();
        setActionNotice(
          successCount === 1
            ? `Created quotation folder for ${targets.find((quotation) => !failures.some((failure) => failure.startsWith(`${quotation.quotationNumber}:`)))?.quotationNumber || 'the selected quotation'}.`
            : `Created quotation folders for ${successCount} quotations.`
        );
      }

      if (failures.length > 0) {
        playError();
        setActionError(`Folder creation failed for ${failures.length} quotation${failures.length === 1 ? '' : 's'}: ${failures.join(' | ')}`);
      }
    } finally {
      setActionBusy(null);
    }
  };

  const handleAcceptQuotation = async (quotation: any) => {
    if (!quotation || actionBusy || !isPendingQuotation(quotation)) return;

    const quotationKey = String(quotation.quotationNumber || quotation.quotationId || quotation.id || '').trim();
    if (!quotationKey) {
      setActionNotice(null);
      setActionError('Quotation number is required before accepting this quotation.');
      playError();
      return;
    }

    const confirmed = window.confirm(
      `Accept quotation ${quotationKey}?\n\nThis will mark it as accepted, create the linked project, and run the current project folder creation workflow. If the matching SharePoint project folder already exists, acceptance will continue and the duplicate folder step will be skipped.`
    );
    if (!confirmed) {
      return;
    }

    setActionBusy('accept');
    setAcceptingQuotationId(String(quotation.id));
    setActionError(null);
    setActionNotice(null);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const result = await azureSqlService.acceptWorkflowQuotation({
        quotationId: quotation.quotationId ?? quotation.id,
        quotationNumber: quotation.quotationNumber,
        createSharePointFolders: true,
      });

      const projectId = String(result?.projectId || '').trim();
      const projectNumber = String(result?.projectNumber || '').trim();
      const sharePointResult = result?.sharePointPlan;
      const sharePointMessage = String(sharePointResult?.message || '').trim();
      const sharePointStatus = String(sharePointResult?.integrationStatus || '').trim().toLowerCase();
      const sharePointFailed = sharePointStatus === 'error';
      const projectsFilterHint = currentUserEmail === WILSON_EMAIL
        ? ' Projects page may default to Civil only.'
        : currentUserEmail === YUAN_EMAIL
          ? ' Projects page may default to Structure only.'
          : '';

      await loadQuotations();

      setActionNotice(
        projectNumber
          ? `Accepted quotation ${quotationKey} and created project ${projectNumber}${projectId ? ` (#${projectId})` : ''}.${projectsFilterHint}`
          : `Accepted quotation ${quotationKey} and created the linked project.${projectsFilterHint}`
      );

      if (sharePointFailed) {
        setActionError(
          sharePointMessage
            ? `Project folder provisioning needs attention: ${sharePointMessage}`
            : 'Project folder provisioning needs attention.'
        );
        playError();
      } else if (sharePointStatus === 'not_configured') {
        setActionError(
          sharePointMessage
            ? `Project created, but project folder provisioning is not configured: ${sharePointMessage}`
            : 'Project created, but project folder provisioning is not configured.'
        );
        playError();
      } else if (sharePointMessage) {
        setActionNotice((current) => current ? `${current} ${sharePointMessage}` : sharePointMessage);
        playSuccess();
      } else {
        playSuccess();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const statusCode = Number((err as { status?: unknown } | null)?.status);

      if (statusCode !== 409) {
        const refreshedQuotations = await loadQuotations(searchQuery.trim(), {
          silent: true,
          forceRefresh: true,
        });
        const reconciledQuotation = findReconciledAcceptedQuotation(
          quotation,
          refreshedQuotations || []
        );

        if (reconciledQuotation) {
          setActionError(null);
          setActionNotice(
            `Accepted quotation ${quotationKey} and created its linked project. ` +
            'The backend response failed after the action completed, so review the project folder if its status is unclear.'
          );
          playSuccess();
          return;
        }
      }

      setActionNotice(null);
      setActionError(`Failed to accept quotation ${quotationKey}: ${message}`);
      playError();
    } finally {
      setAcceptingQuotationId(null);
      setActionBusy(null);
    }
  };

  const handleDeleteQuotations = async (targets: any[]) => {
    if (!targets.length || actionBusy) return;

    const blockedTargets = targets.filter(isAcceptedQuotation);
    const deletableTargets = targets.filter((quotation: any) => !isAcceptedQuotation(quotation));

    if (deletableTargets.length === 0) {
      setActionNotice(null);
      setActionError('Accepted quotations linked to projects cannot be deleted from the quotations screen.');
      playError();
      return;
    }

    const confirmationNumbers = deletableTargets
      .map((quotation: any) => String(quotation.quotationNumber || quotation.quotationId || quotation.id || '').trim())
      .filter(Boolean);
    if (!confirmationNumbers.length || confirmationNumbers.length !== deletableTargets.length) {
      setActionNotice(null);
      setActionError('Quotation number is required before deleting the selected quotation records.');
      playError();
      return;
    }

    if (!confirmDeletionByTyping({
      entityLabel: deletableTargets.length === 1
        ? `quotation ${confirmationNumbers[0]}`
        : `${deletableTargets.length} quotations`,
      confirmationLabel: deletableTargets.length === 1 ? 'quotation number' : 'quotation numbers',
      identifiers: confirmationNumbers,
      softDelete: true,
      extraMessage: 'This only marks the quotation rows as deleted in the database. Deletion will be blocked if a quotation folder already exists in SharePoint.',
    })) {
      return;
    }

    setActionBusy('delete');
    setActionError(null);
    setActionNotice(null);
    setActionBlockedFolders([]);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const failedDeletes: string[] = [];
      const blockedFolders: BlockedQuotationFolder[] = [];
      const deletedKeys = new Set<string>();
      let deletedCount = 0;
      const markDeleted = (quotation: any) => {
        [quotation.id, quotation.quotationId].forEach((value) => {
          const key = String(value || '').trim();
          if (key) deletedKeys.add(key);
        });
        deletedCount += 1;
      };
      const deleteTarget = async (quotation: any) => {
        try {
          await azureSqlService.deleteQuotation(quotation.quotationId ?? quotation.id);
          markDeleted(quotation);
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          const errorBody = (err as any)?.details;
          const errorCode = String(errorBody?.error?.code || (err as any)?.code || '').trim();
          const errorDetails = errorBody?.error?.details || {};
          if (errorCode === 'quotation_delete_blocked_folder_exists') {
            blockedFolders.push({
              quotationNumber: String(errorDetails?.quotationNumber || quotation.quotationNumber || '').trim(),
              folderPath: errorDetails?.folderPath || null,
              webUrl: errorDetails?.webUrl || null,
            });
          }
          failedDeletes.push(`${quotation.quotationNumber}: ${message}`);
        }
      };

      for (let index = 0; index < deletableTargets.length; index += DELETE_CONCURRENCY) {
        await Promise.all(deletableTargets.slice(index, index + DELETE_CONCURRENCY).map(deleteTarget));
      }

      if (deletedCount > 0) {
        playSuccess();
        setActionNotice(
          deletedCount === 1
            ? 'Soft-deleted 1 quotation.'
            : `Soft-deleted ${deletedCount} quotations.`
        );
        setSelectedQuotations((current) => {
          const next = new Set(current);
          deletedKeys.forEach((id) => next.delete(id));
          return next;
        });
        setQuotations((current) => current.filter((quotation: any) => (
          !deletedKeys.has(String(quotation.id || '').trim())
          && !deletedKeys.has(String(quotation.quotationId || '').trim())
        )));
        setPagination((current) => {
          const totalRecords = Math.max(0, Number(current.totalRecords || 0) - deletedCount);
          const nextPageSize = Math.max(1, Number(current.pageSize || pageSize || LIST_PAGE_SIZE));
          return {
            ...current,
            totalRecords,
            totalPages: Math.max(1, Math.ceil(totalRecords / nextPageSize)),
          };
        });
        void loadQuotations(searchQuery.trim(), { silent: true });
      }

      const skippedAcceptedMessage = blockedTargets.length > 0
        ? `${blockedTargets.length} accepted quotation${blockedTargets.length === 1 ? '' : 's'} skipped.`
        : null;
      const failedDeletesMessage = failedDeletes.length > 0
        ? `Delete failed for ${failedDeletes.length} quotation${failedDeletes.length === 1 ? '' : 's'}: ${failedDeletes.join(' | ')}`
        : null;
      const combinedError = [skippedAcceptedMessage, failedDeletesMessage].filter(Boolean).join(' ');
      if (combinedError) {
        setActionError(combinedError);
        setActionBlockedFolders(blockedFolders);
        if (deletedCount === 0) {
          playError();
        }
      }
    } finally {
      setActionBusy(null);
    }
  };

  return (
    <div className="dgf-page quotation-list-page">
      <PageHeader
        title="Quotations"
        subtitle="Track quotes, acceptance status, and pricing at a glance."
        itemCount={visibleQuotations.length}
        gradientColor="linear-gradient(135deg, #173b84 0%, #2251b4 54%, #4b6fbc 100%)"
        icon="📋"
        userLabel={user?.userDetails}
        roleLabel={roleLabel}
        searchEnabled={true}
        searchQuery={searchQuery}
        searchPlaceholder="Search quotations (address, client, amount...)"
        onSearchChange={setSearchQuery}
        sortEnabled={true}
        sortBy={sortBy}
        sortDirection={sortDirection}
        sortOptions={[
          { value: 'quotationNumber', label: 'Quotation Number' },
          { value: 'createdDate', label: 'Creation Date' },
          { value: 'structuralFee', label: 'Structural Fee' },
          { value: 'civilFee', label: 'Civil Fee' },
          { value: 'status', label: 'Status' },
        ]}
        onSortChange={handleSortChange}
        onSortDirectionToggle={toggleSortDirection}
        actions={[
          {
            label: 'Workflow',
            onClick: () => navigate('/quotations/workflow'),
            variant: 'secondary',
          },
          ...(selectMode ? [
            {
              label: actionBusy === 'folder'
                ? 'Creating...'
                : selectedQuotations.size > 1
                  ? `Create Quotation Folders (${selectedQuotations.size})`
                  : 'Create Quotation Folder',
              onClick: () => void handleCreateFolders(selectedQuotationRecords),
              variant: 'secondary' as const,
              disabled: selectedQuotations.size === 0 || actionBusy !== null,
            },
            {
              label: actionBusy === 'delete'
                ? 'Deleting...'
                : selectedQuotations.size > 1
                  ? `Delete (${selectedQuotations.size})`
                  : 'Delete',
              onClick: () => void handleDeleteQuotations(selectedQuotationRecords),
              variant: 'danger' as const,
              disabled: selectedQuotations.size === 0 || actionBusy !== null,
            },
          ] : []),
          {
            label: selectMode ? `Selected (${selectedQuotations.size})` : 'Select',
            onClick: toggleSelectMode,
            variant: selectMode ? 'primary' : 'secondary',
            disabled: actionBusy !== null,
          },
        ]}
        selectEnabled={selectMode}
        selectedCount={selectedQuotations.size}
        totalCount={visibleQuotations.length}
        onSelectAll={toggleSelectAll}
        filters={(
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', width: isNarrowScreen ? '100%' : 'auto' }}>
            <div style={{ minWidth: isNarrowScreen ? '100%' : '180px' }}>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
                density="compact"
                fullWidth
                options={[
                  { value: 'all', label: 'All statuses' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'approved', label: 'Accepted' },
                  { value: 'rejected', label: 'Rejected' },
                  { value: 'draft', label: 'Draft' },
                ]}
              />
            </div>
            <div style={{ minWidth: isNarrowScreen ? 'calc(50% - 5px)' : '150px' }}>
              <Input
                type="date"
                density="compact"
                fullWidth
                value={dateFrom}
                onChange={(e) => setDateFrom(normalizeDateOnlyInput(e.target.value))}
              />
            </div>
            <div style={{ minWidth: isNarrowScreen ? 'calc(50% - 5px)' : '150px' }}>
              <Input
                type="date"
                density="compact"
                fullWidth
                value={dateTo}
                onChange={(e) => setDateTo(normalizeDateOnlyInput(e.target.value))}
              />
            </div>
            {hasActiveDateRange ? (
              <Button
                variant="secondary"
                shape="pill"
                onClick={() => {
                  setDateFrom('');
                  setDateTo('');
                }}
              >
                Clear Dates
              </Button>
            ) : null}
          </div>
        )}
      />

      <PageSection width="wide" padding="compact">
        {(aiFocus || hasActiveDateRange) ? (
          <Card padding="md">
            <div className="dgf-notice__row">
              <div className="dgf-notice dgf-notice--info" style={{ flex: 1 }}>
                {isUsingAiPeriodRange ? (
                  <>
                    Showing Company AI quotations for <strong>{aiPeriodFilter.label}</strong> ({aiPeriodFilter.startDate} to {aiPeriodFilter.endDate}) by <strong>{aiPeriodFilter.dateBasis || 'issue date'}</strong>.
                  </>
                ) : hasActiveDateRange ? (
                  <>
                    Filtering quotations from <strong>{dateFrom || 'any start'}</strong> to <strong>{dateTo || 'any end'}</strong> by <strong>{aiPeriodFilter?.dateBasis || 'issue date'}</strong>.
                  </>
                ) : (
                  <>
                    Focused from Company AI on <strong>{aiFocus?.title}</strong>. Search and quotation filters were widened so the matched row is easier to find.
                  </>
                )}
              </div>
              {aiFollowUp?.prompt ? (
                <Button
                  variant="secondary"
                  shape="pill"
                  onClick={() => navigate(`/ai?prompt=${encodeURIComponent(aiFollowUp.prompt)}`, {
                    state: {
                      companyAiFollowUp: aiFollowUp,
                    },
                  })}
                >
                  Continue in workspace
                </Button>
              ) : null}
            </div>
          </Card>
        ) : null}

        {actionError ? (
          <Card padding="md">
            <div className="dgf-notice dgf-notice--danger">{actionError}</div>
          </Card>
        ) : null}

        {actionBlockedFolders.length > 0 ? (
          <Card padding="md">
            <div className="dgf-notice dgf-notice--warning">
              <div>Quotation folder exists. Delete the SharePoint folder manually, then delete the quotation row again.</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                {actionBlockedFolders.map((folder) => {
                  const label = folder.quotationNumber || folder.folderPath || 'Open folder';
                  return (
                    <Button
                      key={`${label}-${folder.webUrl || folder.folderPath || ''}`}
                      variant="secondary"
                      shape="pill"
                      disabled={!folder.webUrl}
                      onClick={() => {
                        if (folder.webUrl) {
                          window.open(folder.webUrl, '_blank', 'noopener,noreferrer');
                        }
                      }}
                    >
                      Open {label}
                    </Button>
                  );
                })}
              </div>
            </div>
          </Card>
        ) : null}

        {actionNotice ? (
          <Card padding="md">
            <div className="dgf-notice dgf-notice--success">{actionNotice}</div>
          </Card>
        ) : null}

        {error && (
          <Card padding="lg">
            <div className="dgf-state-card">
              <div className="dgf-state-card__icon">⚠️</div>
              <div className="dgf-state-card__title">Error Loading Quotations</div>
              <div className="dgf-notice dgf-notice--danger" style={{ marginTop: 16 }}>{error}</div>
              <div className="dgf-state-card__actions">
                <Button onClick={() => void loadQuotations()} variant="secondary" shape="pill">
                  Retry
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      await login();
                      setTimeout(() => loadQuotations(), 1000);
                    } catch (err) {
                      console.error('Re-authentication failed:', err);
                    }
                  }}
                  variant="primary"
                  shape="pill"
                >
                  Re-authenticate
                </Button>
              </div>
            </div>
          </Card>
        )}

        {loading && (
          <Card padding="lg">
            <div className="dgf-state-card">
              <div className="dgf-state-card__icon">⏳</div>
              <div>Loading quotations...</div>
            </div>
          </Card>
        )}

        {!loading && visibleQuotations.length > 0 && (
          <div className="quotation-data-list">
            {visibleQuotations.map((quotation: any) => {
              const ageDays = String(quotation.statusCode || '').toLowerCase() === '3'
                ? null
                : daysSince(quotation.createdDate);
              const ageStyle = ageDays === null ? null : getAgeBadgeStyle(ageDays);
              const detailBits = [];
              if (quotation.projectScope) detailBits.push(String(quotation.projectScope));
              else if (quotation.projectType) detailBits.push(String(quotation.projectType));
              if (quotation.noUnits !== null && quotation.noUnits !== undefined && String(quotation.noUnits) !== '') detailBits.push(`Units ${quotation.noUnits}`);
              if (quotation.council) detailBits.push(String(quotation.council));
              if (quotation.broughtBy) detailBits.push(`Brought ${quotation.broughtBy}`);
              if (quotation.certifiedBy) detailBits.push(`Cert ${quotation.certifiedBy}`);
              const isAiFocusTarget = matchesCompanyAiFocusRecord(aiFocus, quotation.quotationId ?? quotation.id);

              const clientLabel = [quotation.clientName, quotation.companyName ? `(${quotation.companyName})` : '']
                .filter(Boolean)
                .join(' ') || 'Client pending';
              const descriptionBits = [
                detailBits.length > 0 ? detailBits.join(' · ') : null,
                `Created ${formatDate(quotation.createdDate)}`,
                `Structural ${formatMoney(quotation.structuralFee)}`,
                `Civil ${formatMoney(quotation.civilFee)}`,
              ].filter(Boolean);
              const ageLabel = ageDays !== null ? `${ageDays}d old` : 'Accepted';

              return (
                <DataRow
                  key={quotation.id}
                  id={getCompanyAiFocusDomId('quotations', quotation.quotationId ?? quotation.id) || undefined}
                  className={isAiFocusTarget ? 'quotation-list-row quotation-data-row dgf-row-focus-target' : 'quotation-list-row quotation-data-row'}
                  density="compact"
                  onClick={() => {
                    if (selectMode) {
                      toggleSelection(String(quotation.id));
                      return;
                    }
                    navigate(`/quotations/${quotation.id}`);
                  }}
                  selectedControl={selectMode ? (
                    <input
                      type="checkbox"
                      checked={selectedQuotations.has(String(quotation.id))}
                      onChange={() => toggleSelection(String(quotation.id))}
                      onClick={(event) => event.stopPropagation()}
                      className="invoice-data-row__checkbox"
                      aria-label={`Select quotation ${quotation.quotationNumber}`}
                    />
                  ) : null}
                  title={(
                    <div className="quotation-data-row__title-line">
                      <span className="quotation-data-row__number">{quotation.quotationNumber}</span>
                      <span className="quotation-data-row__location">{quotation.projectLocation || 'Project address pending'}</span>
                    </div>
                  )}
                  subtitle={clientLabel}
                  description={<span className="quotation-data-row__compact-meta">{descriptionBits.join('   ')}</span>}
                  amount={(
                    <div className="quotation-data-row__amount-line">
                      <span>{formatMoney(quotation.quotationAmount)}</span>
                    </div>
                  )}
                  status={(
                    <div className="quotation-data-row__status-line">
                      {isPendingQuotation(quotation) ? (
                        <button
                          type="button"
                          className="quotation-data-row__status-action ui-status-badge ui-status-badge--pending ui-status-badge--sm"
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleAcceptQuotation(quotation);
                          }}
                          disabled={actionBusy !== null}
                          title="Accept quotation"
                          aria-label={`Accept quotation ${quotation.quotationNumber}`}
                        >
                          {actionBusy === 'accept' && acceptingQuotationId === String(quotation.id)
                            ? 'Accepting...'
                            : (quotation.acceptance || quotation.status)}
                        </button>
                      ) : (
                        <StatusBadge status={quotation.acceptance || quotation.status} size="sm">
                          {quotation.acceptance || quotation.status}
                        </StatusBadge>
                      )}
                    </div>
                  )}
                  meta={ageDays !== null && ageStyle ? (
                    <span
                      className="quotation-data-row__age"
                      style={{
                        backgroundColor: ageStyle.bg,
                        borderColor: ageStyle.bg,
                        color: ageStyle.fg,
                      }}
                      title="Days since created"
                    >
                      {ageLabel}
                    </span>
                  ) : (
                    <span className="quotation-data-row__age quotation-data-row__age--muted">{ageLabel}</span>
                  )}
                  footer={!selectMode && isAcceptedQuotationLike(quotation) ? (
                    <div className="quotation-data-row__actions" onClick={(event) => event.stopPropagation()}>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        iconBefore={<Mail aria-hidden="true" />}
                        onClick={() => setEmailTarget({
                          entityType: 'quotation',
                          entityId: Number(quotation.quotationId ?? quotation.id),
                          label: quotation.quotationNumber,
                        })}
                      >
                        Email
                      </Button>
                    </div>
                  ) : null}
                />
              );
            })}
            <ListPaginationControls
              page={pagination.page}
              pageSize={pagination.pageSize}
              totalPages={pagination.totalPages}
              totalRecords={pagination.totalRecords}
              currentCount={visibleQuotations.length}
              pageSizeOptions={LIST_PAGE_SIZE_OPTIONS}
              onPageChange={setPage}
              onPageSizeChange={(nextPageSize) => {
                setPageSize(nextPageSize);
                setPage(1);
              }}
            />
          </div>
        )}

        {!loading && visibleQuotations.length === 0 && !error && (
          <Card padding="none">
            <EmptyState
              icon={<span style={{ fontSize: '64px' }}>📋</span>}
              title="No quotations found"
              description={searchQuery ? 'Try adjusting your search or filters.' : 'Get started by creating your first quotation.'}
            />
          </Card>
        )}
      </PageSection>
      <EmailSendModal
        open={Boolean(emailTarget)}
        target={emailTarget}
        onClose={() => setEmailTarget(null)}
      />
    </div>
  );
};

export default QuotationList;
